import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchMember, PersonRef } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { toLocalInput } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

type BaptismStatus = 'solicitud' | 'entrevista' | 'preparacion' | 'autorizado' | 'programado' | 'bautizado' | 'cancelado';
type PresentationStatus = 'solicitada' | 'aprobada' | 'programada' | 'realizada' | 'rechazada';
type Overview = {
  canManage: boolean;
  canPrepareCommunion: boolean;
  baptisms: { id: string; person: PersonRef; status: BaptismStatus; testimony: string | null; interviewAt: string | null; scheduledOn: string | null; baptizedOn: string | null; minister: string | null; notes: { author: PersonRef; body: string; createdAt: string }[] | null }[];
  presentations: { id: string; childName: string; childBirth: string | null; parents: string; guardians: string | null; preferredDate: string | null; scheduledOn: string | null; minister: string | null; status: PresentationStatus; notes: string | null; requestedBy: PersonRef }[];
  communions: { id: string; heldOn: string; notes: string | null; tasks: { id: string; title: string; person: PersonRef | null; done: boolean }[] }[];
};
const B_LABEL: Record<BaptismStatus, string> = { solicitud: 'Solicitud', entrevista: 'Entrevista', preparacion: 'Preparación', autorizado: 'Autorizado', programado: 'Programado', bautizado: 'Bautizado', cancelado: 'Cancelado' };
const P_LABEL: Record<PresentationStatus, string> = { solicitada: 'Solicitada', aprobada: 'Aprobada', programada: 'Programada', realizada: 'Realizada', rechazada: 'No procede' };
const fmt = (d: string | null) => (d ? d.split('-').reverse().join('/') : '—');

/** Bautismos, presentación de niños y Santa Cena. */
export function OrdinancesPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [params, setParams] = useSearchParams();
  const tab = params.get('ver') ?? 'bautismos';
  const data = useAsync((signal) => (churchId ? api.get<Overview>(`/worship/churches/${churchId}/ordinances`, signal) : Promise.resolve(null)), [churchId]);
  const people = useAsync(
    (signal) => (churchId && data.status === 'ready' && data.data?.canManage ? api.get<{ items: ChurchMember[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items) : Promise.resolve([] as ChurchMember[])),
    [churchId, data.status === 'ready' && data.data?.canManage],
  );
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const o = data.data;
  if (!o) return null;
  const base = `/worship/churches/${churchId}`;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const tabs: [string, string][] = [['bautismos', 'Bautismos'], ['presentaciones', 'Presentación de niños'], ...(o.canPrepareCommunion ? ([['santa-cena', 'Santa Cena']] as [string, string][]) : [])];
  const members = people.status === 'ready' ? people.data.map((p) => ({ personId: p.personId, name: `${p.givenName} ${p.familyName}`.trim() })) : [];
  const myOpen = o.baptisms.find((b) => b.person.personId === me.personId && b.status !== 'bautizado' && b.status !== 'cancelado');

  return (
    <div className="page">
      <header className="page__header">
        <h1>Bautismos, presentación de niños y Santa Cena</h1>
        <p className="muted">Bautismos, presentación de niños y Santa Cena.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <div className="tabs tabs--scroll" role="tablist" aria-label="Bautismos, presentación de niños y Santa Cena">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams(k === 'bautismos' ? {} : { ver: k }, { replace: true })}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'bautismos' ? (
        <>
          {!myOpen && !o.baptisms.some((b) => b.person.personId === me.personId && b.status === 'bautizado') ? (
            <Card title="¿Quieres bautizarte?">
              <form
                className="form"
                onSubmit={(e: FormEvent<HTMLFormElement>) => {
                  e.preventDefault();
                  const testimony = String(new FormData(e.currentTarget).get('testimony') ?? '').trim() || null;
                  void run(() => api.post(`${base}/baptisms`, { testimony }), 'Recibimos tu solicitud. Pastoral te contactará.');
                }}
              >
                <div className="field">
                  <label htmlFor="bap-test">Cuéntanos brevemente tu testimonio (opcional)</label>
                  <textarea id="bap-test" name="testimony" rows={3} maxLength={2000} />
                </div>
                <Button type="submit" busy={busy}>
                  Solicitar bautismo
                </Button>
              </form>
            </Card>
          ) : null}
          {o.canManage && members.length ? (
            <Card title="Registrar solicitud de alguien más">
              <form
                className="family-add"
                onSubmit={(e) => {
                  e.preventDefault();
                  const personId = String(new FormData(e.currentTarget).get('personId') ?? '');
                  if (personId) void run(() => api.post(`${base}/baptisms`, { personId }), 'Solicitud registrada.');
                }}
              >
                <div className="field">
                  <label htmlFor="bap-person">Persona</label>
                  <SearchSelect id="bap-person" name="personId" options={personOptions(members)} />
                </div>
                <Button type="submit" busy={busy}>
                  Registrar
                </Button>
              </form>
            </Card>
          ) : null}
          {o.baptisms.length === 0 ? <Card><EmptyState title="No hay solicitudes de bautismo." /></Card> : null}
          {o.baptisms.map((b) => (
            <Card key={b.id} title={b.person.name}>
              <ol className="steps" aria-label="Proceso">
                {(['solicitud', 'entrevista', 'preparacion', 'autorizado', 'programado', 'bautizado'] as BaptismStatus[]).map((s, i, all) => (
                  <li key={s} className={all.indexOf(b.status) >= i ? 'steps__done' : ''} aria-current={b.status === s ? 'step' : undefined}>
                    {B_LABEL[s]}
                  </li>
                ))}
              </ol>
              <p className="small muted">
                {b.status === 'cancelado' ? 'Cancelado · ' : ''}
                {b.scheduledOn ? `Fecha: ${fmt(b.scheduledOn)}` : ''}
                {b.minister ? ` · Oficia: ${b.minister}` : ''}
                {b.interviewAt ? ` · Entrevista: ${new Date(b.interviewAt).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
              </p>
              {b.testimony ? <p className="pre-line small">{b.testimony}</p> : null}
              {o.canManage ? <BaptismEditor b={b} busy={busy} onSave={(body) => run(() => api.put(`${base}/baptisms/${b.id}`, body), 'Proceso actualizado.')} /> : null}
            </Card>
          ))}
        </>
      ) : null}

      {tab === 'presentaciones' ? (
        <>
          <Card title="Solicitar la presentación de un niño o niña">
            <form
              className="form"
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                const t = (k: string) => String(f.get(k) ?? '').trim() || null;
                void run(
                  () => api.post(`${base}/presentations`, { childName: t('childName') ?? '', childBirth: t('childBirth'), parents: t('parents') ?? '', guardians: t('guardians'), preferredDate: t('preferredDate'), notes: t('notes') }),
                  'Recibimos la solicitud. Pastoral la revisará.',
                ).then((ok) => ok && (e.target as HTMLFormElement).reset());
              }}
            >
              <div className="form__row">
                <Field label="Nombre del niño o niña" name="childName" required maxLength={150} />
                <Field label="Fecha de nacimiento" name="childBirth" type="date" />
              </div>
              <Field label="Papá y mamá" name="parents" required maxLength={300} placeholder="Beto y Carla López" />
              <Field label="Tutores (si aplica)" name="guardians" maxLength={300} />
              <Field label="Fecha que prefieren (opcional)" name="preferredDate" type="date" />
              <Field label="Comentarios (opcional)" name="notes" maxLength={1000} />
              <Button type="submit" busy={busy}>
                Enviar solicitud
              </Button>
            </form>
          </Card>
          {o.presentations.map((p) => (
            <Card key={p.id} title={p.childName}>
              <p className="small">
                <span className="badge">{P_LABEL[p.status]}</span> Padres: {p.parents}
                {p.guardians ? ` · Tutores: ${p.guardians}` : ''}
              </p>
              <p className="small muted">
                Nació: {fmt(p.childBirth)} · Prefieren: {fmt(p.preferredDate)} · Programada: {fmt(p.scheduledOn)}
                {p.minister ? ` · Oficia: ${p.minister}` : ''} · Pidió: {p.requestedBy.name}
              </p>
              {p.notes ? <p className="small">{p.notes}</p> : null}
              {o.canManage ? (
                <form
                  className="form__row food-row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
                    void run(() => api.put(`${base}/presentations/${p.id}`, { status: t('status'), scheduledOn: t('scheduledOn'), minister: t('minister') }), 'Presentación actualizada.');
                  }}
                >
                  <div className="field">
                    <label htmlFor={`ps-${p.id}`}>Estado</label>
                    <select id={`ps-${p.id}`} name="status" defaultValue={p.status}>
                      {(Object.keys(P_LABEL) as PresentationStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {P_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field label="Fecha" name="scheduledOn" type="date" defaultValue={p.scheduledOn ?? p.preferredDate ?? ''} />
                  <Field label="Oficia" name="minister" maxLength={120} defaultValue={p.minister ?? ''} />
                  <Button type="submit" busy={busy}>
                    Guardar
                  </Button>
                </form>
              ) : null}
            </Card>
          ))}
        </>
      ) : null}

      {tab === 'santa-cena' && o.canPrepareCommunion ? <CommunionTab o={o} base={base} run={run} busy={busy} members={members} /> : null}
    </div>
  );
}

function BaptismEditor({ b, busy, onSave }: { b: Overview['baptisms'][number]; busy: boolean; onSave: (body: object) => void }) {
  return (
    <details className="pastoral-edit">
      <summary>Llevar el proceso (Pastoral)</summary>
      {b.notes && b.notes.length ? (
        <ul className="list">
          {b.notes.map((n, i) => (
            <li key={i} className="list__item">
              <span className="agenda__body">
                <span>{n.body}</span>
                <span className="muted small">{n.author.name}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const t = (k: string) => String(f.get(k) ?? '').trim() || null;
          onSave({ status: t('status'), interviewAt: t('interviewAt') ? new Date(t('interviewAt')!).toISOString() : null, scheduledOn: t('scheduledOn'), baptizedOn: t('baptizedOn'), minister: t('minister'), note: t('note') });
        }}
      >
        <div className="form__row">
          <div className="field">
            <label htmlFor={`bs-${b.id}`}>Paso</label>
            <select id={`bs-${b.id}`} name="status" defaultValue={b.status}>
              {(Object.keys(B_LABEL) as BaptismStatus[]).map((s) => (
                <option key={s} value={s}>
                  {B_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <Field label="Entrevista" name="interviewAt" type="datetime-local" defaultValue={b.interviewAt ? toLocalInput(new Date(b.interviewAt)) : ''} />
        </div>
        <div className="form__row form__row--3">
          <Field label="Fecha del bautismo" name="scheduledOn" type="date" defaultValue={b.scheduledOn ?? ''} />
          <Field label="Bautizado el" name="baptizedOn" type="date" defaultValue={b.baptizedOn ?? ''} />
          <Field label="Oficia" name="minister" maxLength={120} defaultValue={b.minister ?? ''} />
        </div>
        <Field label="Nota pastoral (privada)" name="note" maxLength={2000} />
        <Button type="submit" busy={busy}>
          Guardar
        </Button>
      </form>
    </details>
  );
}

function CommunionTab({ o, base, run, busy, members }: { o: Overview; base: string; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean>; busy: boolean; members: PersonRef[] }) {
  const [date, setDate] = useState('');
  const [extra, setExtra] = useState<Record<string, string>>({});
  const people = members.length ? members : [...new Map(o.communions.flatMap((c) => c.tasks.map((t) => t.person)).filter((p): p is PersonRef => p !== null).map((p) => [p.personId, p])).values()];
  return (
    <>
      <Card title="Preparar una Santa Cena">
        <p className="small muted">Solo organizamos la preparación. KEORA nunca registra quién participa.</p>
        <div className="inline-form">
          <label className="visually-hidden" htmlFor="cm-date">
            Fecha
          </label>
          <input id="cm-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Button busy={busy} disabled={!date} onClick={() => void run(() => api.post(`${base}/communions`, { heldOn: date }), 'Santa Cena creada con su checklist.')}>
            Crear
          </Button>
        </div>
      </Card>
      {o.communions.map((c) => (
        <Card key={c.id} title={`Santa Cena · ${fmt(c.heldOn)}`}>
          <ul className="checklist">
            {c.tasks.map((t) => (
              <li key={t.id} className="checklist__item food-item">
                <label className="check checklist__body">
                  <input type="checkbox" checked={t.done} disabled={busy} onChange={() => void run(() => api.put(`${base}/communion-tasks/${t.id}`, { personId: t.person?.personId ?? null, done: !t.done }), 'Listo.')} />
                  <span className={t.done ? 'checklist__title muted' : 'checklist__title'}>{t.title}</span>
                </label>
                <label className="visually-hidden" htmlFor={`ct-${t.id}`}>
                  Responsable de {t.title}
                </label>
                <SearchSelect
                  id={`ct-${t.id}`}
                  value={t.person?.personId ?? ''}
                  disabled={busy}
                  onChange={(v) => void run(() => api.put(`${base}/communion-tasks/${t.id}`, { personId: v || null, done: t.done }), 'Responsable asignado.')}
                  emptyLabel="Sin responsable"
                  options={personOptions(people)}
                />
              </li>
            ))}
          </ul>
          <div className="inline-form">
            <label className="visually-hidden" htmlFor={`cx-${c.id}`}>
              Otra tarea
            </label>
            <input id={`cx-${c.id}`} placeholder="Otra tarea…" value={extra[c.id] ?? ''} maxLength={120} onChange={(e) => setExtra((x) => ({ ...x, [c.id]: e.target.value }))} />
            <Button variant="secondary" disabled={!extra[c.id]?.trim()} onClick={() => void run(() => api.post(`${base}/communions/${c.id}/tasks`, { title: extra[c.id]!.trim() }), 'Tarea agregada.').then(() => setExtra((x) => ({ ...x, [c.id]: '' })))}>
              Agregar
            </Button>
          </div>
        </Card>
      ))}
    </>
  );
}
